import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Lock, Search, Users, X } from 'lucide-react';
import type { Meeting } from '../../types';
import type { ChatThreadDTO } from '../../global';
import { ChatThreadList } from '../chat/ChatThreadList';
import { cn } from '../../components/ui/cn';
import { dateLocale, t } from '../../lib/i18n';

/** Kayıtlı sohbetler (sunucuda şifreli geçmiş); verilirse çekmecede "Sohbetler" sekmesi açılır. */
export interface MobileChatHistory {
  threads: ChatThreadDTO[];
  activeId: string | null;
  onNew: () => void;
  onOpen: (id: string) => void;
  onRename: (id: string, title: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onDeleteAll: () => Promise<void>;
}

interface MobileMeetingsDrawerProps {
  open: boolean;
  onClose: () => void;
  meetings: Meeting[];
  onSelectMeeting: (meeting: Meeting) => void;
  chats?: MobileChatHistory;
}

/**
 * Sohbetteki soldan açılan çekmece: kayıtlı sohbetler ve geçmiş toplantılar. Toplantılar masaüstü
 * kenar çubuğundaki gibi "Özel" ve "Paylaşılanlar" altında. Geri tuşu (Escape) ve dışarıya
 * dokunmak kapatır.
 */
export const MobileMeetingsDrawer: React.FC<MobileMeetingsDrawerProps> = ({ open, onClose, meetings, onSelectMeeting, chats }) => {
  const [tab, setTab] = useState<'chats' | 'meetings'>('chats');
  const [query, setQuery] = useState('');
  const [privateOpen, setPrivateOpen] = useState(true);
  const [sharedOpen, setSharedOpen] = useState(true);

  // Çekmece her açılışta sohbetlerle başlar.
  useEffect(() => {
    if (open) setTab('chats');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase(dateLocale());
    const sorted = [...meetings].sort((a, b) => b.recordedAtISO.localeCompare(a.recordedAtISO));
    return needle ? sorted.filter((m) => m.title.toLocaleLowerCase(dateLocale()).includes(needle)) : sorted;
  }, [meetings, query]);

  if (!open) return null;

  const shortDate = (meeting: Meeting) => {
    const at = new Date(meeting.recordedAtISO);
    if (Number.isNaN(at.getTime())) return meeting.date;
    if (at.toDateString() === new Date().toDateString()) return t('Bugün', 'Today');
    return at.toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short' });
  };

  const group = (
    label: string,
    icon: React.ReactNode,
    items: Meeting[],
    isOpen: boolean,
    toggle: () => void,
    empty: string
  ) => (
    <div>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={isOpen}
        className="w-full h-11 flex items-center gap-2.5 px-2.5 rounded-xl text-left active:bg-secondary"
      >
        <span className="text-muted-foreground [&_svg]:w-[18px] [&_svg]:h-[18px]">{icon}</span>
        <span className="flex-1 text-[15px] font-semibold text-foreground">{label}</span>
        <span className="text-[13px] text-muted-foreground">{items.length}</span>
        <ChevronDown className={cn('w-[18px] h-[18px] text-muted-foreground transition-transform', !isOpen && '-rotate-90')} />
      </button>
      {isOpen && (
        <div className="ml-[19px] pl-2 border-l border-border">
          {items.length === 0 ? (
            <p className="px-2 py-2.5 text-[14px] italic text-muted-foreground">{empty}</p>
          ) : (
            items.map((meeting) => (
              <button
                key={meeting.id}
                type="button"
                onClick={() => {
                  onSelectMeeting(meeting);
                  onClose();
                }}
                className="w-full h-[46px] flex items-center gap-2.5 px-2 rounded-[10px] text-left active:bg-secondary"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground shrink-0" />
                <span className="flex-1 min-w-0 text-[15px] text-foreground truncate">{meeting.title}</span>
                <span className="text-[12.5px] text-muted-foreground whitespace-nowrap">{shortDate(meeting)}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );

  const showChats = Boolean(chats) && tab === 'chats';

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label={chats ? t('Sohbetler ve toplantılar', 'Chats and meetings') : t('Geçmiş toplantılar', 'Past meetings')}>
      <button
        type="button"
        aria-label={t('Kapat', 'Close')}
        onClick={onClose}
        className="absolute inset-0 w-full bg-black/55"
      />
      <aside className="absolute left-0 top-0 bottom-0 w-[86%] max-w-[330px] bg-card border-r border-border rounded-r-3xl shadow-2xl flex flex-col gap-3.5 pt-5 pb-6 px-3 animate-drawer-in">
        <div className="flex items-center justify-between pl-2">
          <h2 className="text-[20px] font-bold text-foreground">{showChats ? t('Sohbetler', 'Chats') : t('Toplantılar', 'Meetings')}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('Kapat', 'Close')}
            className="w-11 h-11 rounded-full flex items-center justify-center text-muted-foreground active:bg-secondary"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {chats && (
          <div role="tablist" className="mx-1 grid grid-cols-2 gap-1 p-1 rounded-xl bg-secondary">
            {(['chats', 'meetings'] as const).map((id) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={cn(
                  'h-10 rounded-lg text-[14.5px] font-semibold transition-colors',
                  tab === id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
                )}
              >
                {id === 'chats' ? t('Sohbetler', 'Chats') : t('Toplantılar', 'Meetings')}
              </button>
            ))}
          </div>
        )}
        {showChats && chats ? (
          <div className="flex-1 min-h-0">
            <ChatThreadList
              touch
              threads={chats.threads}
              activeId={chats.activeId}
              onNew={() => {
                chats.onNew();
                onClose();
              }}
              onOpen={(id) => {
                chats.onOpen(id);
                onClose();
              }}
              onRename={chats.onRename}
              onDelete={chats.onDelete}
              onDeleteAll={chats.onDeleteAll}
            />
          </div>
        ) : (
          <>
        <label className="mx-1 flex items-center gap-2.5 h-11 px-3 rounded-xl bg-secondary text-muted-foreground">
          <Search className="w-[18px] h-[18px] shrink-0" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('Toplantı ara', 'Search meetings')}
            aria-label={t('Toplantı ara', 'Search meetings')}
            className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[16px] text-foreground placeholder:text-muted-foreground"
          />
        </label>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-1">
          <p className="px-2.5 pt-1 pb-1 text-[12px] font-bold tracking-[0.09em] uppercase text-muted-foreground">
            {t('Alanlar', 'Spaces')}
          </p>
          {group(
            t('Özel', 'Private'),
            <Lock />,
            filtered.filter((m) => m.space !== 'shared'),
            privateOpen,
            () => setPrivateOpen((v) => !v),
            t('Henüz not yok', 'No notes yet')
          )}
          {group(
            t('Paylaşılanlar', 'Shared with me'),
            <Users />,
            filtered.filter((m) => m.space === 'shared'),
            sharedOpen,
            () => setSharedOpen((v) => !v),
            t('Paylaşılan not yok', 'No shared notes')
          )}
        </div>
          </>
        )}
      </aside>
    </div>
  );
};
